import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowDown, ArrowLeft, ArrowUp, Check } from 'lucide-react';
import { OnboardingSpotlight } from './OnboardingSpotlight';
import { cn } from '../../utils/cn';
import { useLanguage } from '../../features/language/useLanguage';
import type { FieldGuideConfig } from './onboardingFieldConfigs';

/**
 * Generic config-driven field-guide overlay used during onboarding.
 *
 * Walk the user through a form field-by-field: each field is spotlighted in
 * turn, "Next" only unlocks once that field passes its `validate()` check,
 * and a standing amber banner reminds the user the data is real.
 *
 * Validation reads the live DOM (not any form's internal state) so the guide
 * stays decoupled from react-hook-form internals.
 *
 * Use `inventoryFieldGuideConfig` (or any other `FieldGuideConfig`) from
 * `onboardingFieldConfigs.ts` to supply the field definitions and copy.
 */
export const OnboardingFieldGuide: React.FC<{
  config: FieldGuideConfig;
  onSkip: () => void;
  /** Take the tour back one step. Used by configs declaring `back: { step: true }`
   *  — a step's first phase, where there is nothing left to undo inside the step. */
  onPrevStep?: () => void;
}> = ({ config, onSkip, onPrevStep }) => {
  const { guideId, fields, submitSelector, banner, reviewTitle, reviewBody, unionSelector, confirmSelector, confirmInstruction, blockOutside, placement, back: backTarget } = config;
  const { t } = useLanguage();
  const navigate = useNavigate();
  // Resolve this guide's copy from the i18n dictionary, falling back to the
  // English source co-located in the config.
  const gk = (suffix: string) => `onboarding.guides.${guideId}.${suffix}`;

  const [idx, setIdx] = useState(0);
  const [valid, setValid] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  // Poll the current field's validity and, in the review phase, whether the
  // confirm button has appeared in the DOM (signals the user clicked the
  // primary submit and a confirmation modal is now open).
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const v = idx < fields.length ? fields[idx].validate() : true;
      setValid((prev) => (prev === v ? prev : v));

      // Action steps auto-advance the instant their result lands, so the user
      // isn't stranded clicking Next on a control that has already disappeared.
      if (v && idx < fields.length && fields[idx].autoAdvance) {
        setIdx((i) => (i === idx ? i + 1 : i));
      }

      // Only poll confirm-button visibility when in the review phase and the
      // config provides a confirmSelector.
      if (idx >= fields.length && confirmSelector) {
        const el = document.querySelector<HTMLElement>(confirmSelector);
        const visible = !!el && el.getBoundingClientRect().width > 0;
        setConfirmVisible((prev) => (prev === visible ? prev : visible));
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [idx, fields, confirmSelector]);

  // On mobile, the soft-keyboard "Next"/"Go" action key advances DOM focus to the
  // following field (or submits the form) natively — but that never runs the
  // guide's next(), so the spotlight stays stranded on the field the user just
  // left (the reported "highlight stuck on Initial Cost" while the cursor jumped
  // to Reorder warning). While the guide is mounted, swallow Enter on plain form
  // controls so the only way forward is the highlighted "Next" button, keeping the
  // spotlight and the focused field in lockstep. Comboboxes are exempt — there
  // Enter picks the active option (it doesn't jump fields) — as are buttons/links
  // (Enter activates them) and textareas (Enter inserts a newline).
  useEffect(() => {
    const swallowEnter = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.isComposing || e.keyCode === 229) return;
      const el = e.target as HTMLElement | null;
      if (!el) return;
      const tag = el.tagName;
      if (tag !== 'INPUT' && tag !== 'SELECT') return;
      if (el.getAttribute('role') === 'combobox') return;
      e.preventDefault();
    };
    document.addEventListener('keydown', swallowEnter, true);
    return () => document.removeEventListener('keydown', swallowEnter, true);
  }, []);

  const onSubmitPhase = idx >= fields.length;
  const current = onSubmitPhase ? null : fields[idx];
  const total = fields.length;
  // In the review phase, if the confirm button is visible, spotlight it instead
  // of the primary submit so the guide follows the flow into the modal.
  const targetSelector = onSubmitPhase
    ? confirmSelector && confirmVisible
      ? confirmSelector
      : submitSelector
    : current!.selector;

  const next = () => {
    if (!onSubmitPhase && valid) setIdx((i) => i + 1);
  };

  // The nearest earlier field the user can actually return to, or -1 when there
  // is none. Auto-advancing fields are SKIPPED: their validate() keeps passing
  // after the action landed ("the draft exists", "the second tab is open"), so
  // the polling effect above would push the guide straight forward again the
  // next frame. That bounce is what made Back look enabled but dead on the
  // work-order bean/roast-type fields and on the packing-tab phase.
  let prevFieldIdx = -1;
  for (let i = Math.min(idx, fields.length) - 1; i >= 0; i--) {
    if (!fields[i].autoAdvance) {
      prevFieldIdx = i;
      break;
    }
  }

  // Falling out of the field list means leaving the phase itself — close the
  // modal, return to the list page, or step the tour back. A config without a
  // `back` target has no way out, and Back is disabled rather than inert.
  const canBack = prevFieldIdx >= 0 || !!backTarget;

  const back = () => {
    if (prevFieldIdx >= 0) {
      setIdx(prevFieldIdx);
      return;
    }
    if (!backTarget) return;
    if ('step' in backTarget) {
      onPrevStep?.();
      return;
    }
    if ('route' in backTarget) {
      navigate(backTarget.route);
      return;
    }
    // Press the real control the user would click by hand. The spotlight refuses
    // pointer events outside its highlight, but it lets untrusted ones through
    // precisely so these scripted presses land — see the isTrusted gate in
    // OnboardingSpotlight's `allowed`.
    if ('element' in backTarget) {
      backTarget.element()?.click();
      return;
    }
    const els = Array.from(document.querySelectorAll<HTMLElement>(backTarget.click));
    const el = els.find((e) => e.getBoundingClientRect().width > 0) ?? els[0];
    el?.click();
  };

  return (
    <OnboardingSpotlight
      targetSelector={targetSelector}
      padding={6}
      placement={placement ?? 'above'}
      unionSelector={unionSelector ?? '[role="listbox"]'}
      centerTarget
      // Some steps (e.g. cupping scoring) need the user to drag a slider next to
      // the halo or switch tabs — keep the page clickable instead of blocking it.
      blockOutside={blockOutside ?? true}
      // Per-field phase: dim everything but the active field to focus the user.
      // Review phase: don't dim — the user needs to read the WHOLE form before
      // committing real data. The Create button still gets its halo highlight.
      dim={!onSubmitPhase}
    >
      {({ offscreen, recenter }) => {
        // The halo tracks its element, so scrolling past it leaves the card
        // pointing off screen. Offer the way back in the slot that has just gone
        // stale — the validity note, or the "use the highlighted button" hint —
        // rather than growing the card with a row that is empty most of the time.
        const backToHighlight = offscreen ? (
          <button
            type="button"
            onClick={recenter}
            data-test-id="onboarding-field-recenter"
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-50 hover:text-amber-800"
          >
            {offscreen === 'above' ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : (
              <ArrowDown className="h-3.5 w-3.5" />
            )}
            {t('onboarding.fieldGuide.backToHighlight', undefined, 'Back to the highlight')}
          </button>
        ) : null;

        return (
        <div
          className="w-full rounded-xl border border-amber-300 bg-white p-4 shadow-2xl"
          data-test-id="onboarding-field-guide"
        >
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
            <p className="text-xs font-medium text-amber-800">
              {t(gk('banner'), undefined, banner)}
            </p>
          </div>

          {onSubmitPhase ? (
            <>
              <p className="mt-3 text-sm font-semibold text-gray-900">{t(gk('reviewTitle'), undefined, reviewTitle)}</p>
              <p className="mt-1 text-sm text-gray-700">
                {confirmSelector && confirmVisible && confirmInstruction
                  ? t(gk('confirmInstruction'), undefined, confirmInstruction)
                  : t(gk('reviewBody'), undefined, reviewBody)}
              </p>
              <div className="mt-3 flex items-center justify-between">
                {confirmSelector && confirmVisible ? (
                  <span />
                ) : (
                  <button
                    onClick={back}
                    disabled={!canBack}
                    className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> {t('onboarding.fieldGuide.back', undefined, 'Back')}
                  </button>
                )}
                {backToHighlight ?? (
                  <span className="text-xs font-medium text-amber-700">
                    {t('onboarding.fieldGuide.useHighlightedButton', undefined, '↑ Use the highlighted button')}
                  </span>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="mt-3 flex items-center gap-2">
                <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-amber-600 px-1.5 text-[11px] font-bold text-white">
                  {idx + 1}
                </span>
                <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                  {t(
                    'onboarding.fieldGuide.fieldLabel',
                    { index: idx + 1, total, label: t(gk(`fields.${current!.key}.label`), undefined, current!.label) },
                    'Field {{index}} of {{total}} · {{label}}',
                  )}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-800">{t(gk(`fields.${current!.key}.instruction`), undefined, current!.instruction)}</p>
              <div className="mt-1 flex items-center justify-between gap-2">
                <p
                  className={cn(
                    'inline-flex items-center gap-1 text-xs font-medium',
                    valid ? 'text-green-600' : 'text-gray-400',
                  )}
                >
                  {valid && <Check className="h-3.5 w-3.5" />}
                  {valid ? t('onboarding.fieldGuide.looksGood', undefined, 'Looks good') : t('onboarding.fieldGuide.fillToContinue', undefined, 'Fill this in to continue')}
                </p>
                {backToHighlight}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <button
                  onClick={back}
                  disabled={!canBack}
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> {t('onboarding.fieldGuide.back', undefined, 'Back')}
                </button>
                <button
                  onClick={next}
                  disabled={!valid}
                  data-test-id="onboarding-field-next"
                  className={cn(
                    'rounded-lg px-4 py-1.5 text-sm font-medium text-white transition-colors',
                    valid
                      ? 'bg-amber-600 hover:bg-amber-700 onboarding-btn-glow'
                      : 'cursor-not-allowed bg-gray-300',
                  )}
                >
                  {idx === total - 1 ? t('onboarding.fieldGuide.reviewAndCreate', undefined, 'Review & create') : t('onboarding.fieldGuide.next', undefined, 'Next')}
                </button>
              </div>
            </>
          )}

          <div className="mt-3 border-t border-gray-100 pt-2 text-center">
            <button
              type="button"
              data-test-id="onboarding-field-skip"
              onClick={onSkip}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              {t('onboarding.fieldGuide.skip', undefined, 'Skip tour')}
            </button>
          </div>
        </div>
        );
      }}
    </OnboardingSpotlight>
  );
};
